/**
 * Mobile Card View for Side-by-Side Scores Matrix
 * Shows all models as stacked cards with 2-column criterion grid
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { CompositeScore } from '../../../hooks/useComparisonScores';
import { getModelLogo } from '../../../utils/logoMapping';
import { formatModelDisplayName } from '../../../utils/modelDisplayName';
import { ScoringRubricModal } from './ScoringRubricModal';

interface SideBySideMatrixMobileProps {
  scores: CompositeScore[];
  onOpenResponse: (modelId: string) => void;
}

// 7 Criteria from user-facing rubric with icons and descriptions
const CRITERIA_CONFIG = [
  { name: 'Accuracy', dbName: 'Accuracy', icon: '🎯', description: 'Facts, math, and claims are correct and consistent.' },
  { name: 'Completeness', dbName: 'Completeness', icon: '✅', description: 'Answers every required part in the requested format.' },
  { name: 'Clarity', dbName: 'Clarity', icon: '📘', description: 'Easy to follow; clear structure and labeling.' },
  { name: 'Relevance', dbName: 'Relevance', icon: '🔍', description: 'Stays on-topic; follows your instructions and constraints.' },
  { name: 'Creativity', dbName: 'Creativity', icon: '💡', description: 'Useful, original ideas—no boilerplate or fluff.' },
  { name: 'Bias & Safety', dbName: 'Balance', icon: '🛡️', description: 'Responsible tone; avoids unsafe or policy-breaking content.' },
  { name: 'Conciseness', dbName: 'Conciseness', icon: '✂️', description: 'Gets to the point; just enough detail to be useful.' }
];

export const SideBySideMatrixMobile: React.FC<SideBySideMatrixMobileProps> = ({
  scores,
  onOpenResponse
}) => {
  const [activeCriterion, setActiveCriterion] = useState<typeof CRITERIA_CONFIG[0] | null>(null);
  const [showRubricModal, setShowRubricModal] = useState(false);
  const lastTriggerRef = useRef<HTMLButtonElement | null>(null);

  const closeCriterionModal = useCallback(() => {
    setActiveCriterion(null);
    if (lastTriggerRef.current) {
      lastTriggerRef.current.focus();
      lastTriggerRef.current = null;
    }
  }, [setActiveCriterion]);

  useEffect(() => {
    if (!activeCriterion) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCriterionModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeCriterion, closeCriterionModal]);

  // Helper to get score for a specific criterion
  const getCriterionScore = (score: CompositeScore, dbName: string): number => {
    const criterionScore = score.criterionScores.find(cs =>
      cs.criterionName.toLowerCase() === dbName.toLowerCase()
    );
    return criterionScore?.score ?? 0;
  };

  const getCriterionColorClass = (score: number): string => {
    if (score >= 9) return 'text-emerald-400 font-semibold';
    if (score >= 8) return 'text-green-400 font-medium';
    if (score >= 7) return 'text-yellow-400';
    if (score >= 6) return 'text-orange-400';
    return 'text-red-400';
  };

  // Find highest overall score for gold highlighting
  const highestScore = Math.max(...scores.map(s => s.compositeScore ?? 0), 0);

  return (
    <>
      <div className="space-y-4">
        {scores
          .filter(s => s.compositeScore != null && s.compositeScore > 0)
          .sort((a, b) => (b.compositeScore ?? 0) - (a.compositeScore ?? 0))
          .map(score => {
            const isHighestScore = score.compositeScore === highestScore && (score.compositeScore ?? 0) > 0;

            return (
              <div
                key={score.modelId}
                className={`bg-slate-800/30 backdrop-blur-sm border rounded-xl overflow-hidden transition-all ${
                  isHighestScore
                    ? 'border-yellow-500/60 ring-2 ring-yellow-500/30 shadow-lg shadow-yellow-500/10'
                    : 'border-slate-700/50'
                }`}
              >
                {/* Card Header */}
                <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-700/30 rounded-lg flex items-center justify-center p-1">
                      <img
                        src={getModelLogo(score.modelId, score.modelName)}
                        alt={`${score.modelName} logo`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <h3 className="font-semibold text-white">
                          {score.modelDisplayName || formatModelDisplayName(score.modelId)}
                        </h3>
                      </div>
                    </div>
                  </div>
                  <span className={`text-sm font-semibold ${getCriterionColorClass(score.compositeScore ?? 0)}`}>
                    {(score.compositeScore ?? 0) > 0 ? (score.compositeScore ?? 0).toFixed(1) : '—'}
                  </span>
                </div>

                {/* Card Body - 2-Column Grid of Criteria */}
                <div className="px-4 pb-4 pt-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full min-w-0">
                    {CRITERIA_CONFIG.map(criterion => {
                      const criterionScore = getCriterionScore(score, criterion.dbName);
                      return (
                        <button
                          key={criterion.dbName}
                          onClick={(e) => {
                            e.stopPropagation();
                            lastTriggerRef.current = e.currentTarget;
                            setActiveCriterion(criterion);
                          }}
                          className="flex w-full min-w-0 items-center justify-between text-left rounded-lg border border-slate-700/60 bg-slate-800/30 px-3 py-2 hover:border-slate-600 hover:bg-slate-700/30 transition-colors"
                          type="button"
                        >
                          <span className="text-sm text-slate-300 flex items-center gap-2 min-w-0">
                            <span className="text-base flex-shrink-0">{criterion.icon}</span>
                            <span className="truncate">{criterion.name}</span>
                          </span>
                          <span className={`text-sm font-semibold flex-shrink-0 ${getCriterionColorClass(criterionScore)}`}>
                            {criterionScore > 0 ? criterionScore.toFixed(1) : '—'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Card Footer - View Full Response Button */}
                <div className="px-4 pb-4">
                  <button
                    onClick={() => onOpenResponse(score.modelId)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-medium transition-colors"
                    aria-label={`View ${score.modelDisplayName || score.modelName} full response`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View Full Response
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* Criterion Description Popup */}
      {activeCriterion && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
          onClick={closeCriterionModal}
          role="dialog"
          aria-modal="true"
          aria-label={`${activeCriterion.name} definition`}
        >
          <div
            className="bg-slate-800 rounded-lg p-6 max-w-md mx-4 border border-slate-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <span className="text-4xl">{activeCriterion.icon}</span>
                <h3 className="text-xl font-bold text-white">{activeCriterion.name}</h3>
              </div>
              <button
                type="button"
                onClick={closeCriterionModal}
                aria-label="Close"
                className="text-slate-400 hover:text-white transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3.5 3.5l9 9m0-9-9 9"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <p className="text-slate-300 leading-relaxed">{activeCriterion.description}</p>
          </div>
        </div>
      )}

      {/* Scoring Rubric Modal */}
      <ScoringRubricModal
        isOpen={showRubricModal}
        onClose={() => setShowRubricModal(false)}
      />
    </>
  );
};
